import axios from "axios";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:4000";

export const api = axios.create({
  baseURL: API_BASE_URL
});

export function setAuthToken(token) {
  if (token) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common.Authorization;
  }
}

function firstValidationMessage(details) {
  if (!details || typeof details !== "object") return "";

  const fieldErrors = details.fieldErrors || {};
  for (const [field, messages] of Object.entries(fieldErrors)) {
    if (Array.isArray(messages) && messages.length > 0) {
      return `${field}: ${messages[0]}`;
    }
  }

  const formErrors = Array.isArray(details.formErrors) ? details.formErrors : [];
  if (formErrors.length > 0) return String(formErrors[0]);

  return "";
}

// Convenience wrapper — returns parsed JSON, throws on non-2xx
export async function apiFetch(path, { method = "GET", body, headers } = {}) {
  const config = { method, headers: headers || {} };
  if (body !== undefined) {
    config.data = body;
  }
  const res = await api.request({ url: path, ...config });
  return res.data;
}

// Raw fetch wrapper for multipart/FormData uploads (bypasses axios JSON serialisation)
export function apiFetchRaw(path, options = {}) {
  const token = api.defaults.headers.common.Authorization;
  const reqHeaders = {};
  if (token) reqHeaders["Authorization"] = token;
  // Do NOT set Content-Type — let the browser set it with the boundary for FormData
  const base = import.meta.env.VITE_API_BASE_URL || "http://localhost:4000";
  return fetch(`${base}${path}`, {
    ...options,
    headers: { ...reqHeaders, ...(options.headers || {}) }
  });
}

export function getApiError(error, fallback = "Something went wrong") {
  const data = error?.response?.data || {};
  const code = data?.error;

  if (code === "VALIDATION_FAILED") {
    const msg = firstValidationMessage(data.details);
    return msg || "Validation failed. Please check your input values.";
  }

  if (code === "POSTING_VALIDATION_FAILED") {
    const issues = data?.readiness?.issues;
    if (Array.isArray(issues) && issues.length > 0) {
      const first = issues[0];
      if (typeof first === "string") return first;
      return first?.message || "Posting validation failed. Please fix bill fields and try again.";
    }
    return "Posting validation failed. Please fix bill fields and try again.";
  }

  if (code === "POSTING_PROFILE_INCOMPLETE") {
    const missing = Array.isArray(data?.missingRequired) ? data.missingRequired : [];
    if (missing.length > 0) {
      return `Tally posting setup incomplete. Missing: ${missing.join(", ")}. Open Settings -> Tally Posting Profile.`;
    }
    return "Tally posting setup is incomplete. Open Settings -> Tally Posting Profile.";
  }

  if (code === "PLAN_UPGRADE_REQUIRED") {
    if (typeof data?.message === "string" && data.message.trim()) return data.message;
    if (Array.isArray(data?.requiredPlans) && data.requiredPlans.length > 0) {
      const current = data?.currentPlan || "free";
      return `This feature requires one of: ${data.requiredPlans.join(", ")}. Your current plan is ${current}.`;
    }
    if (data?.requiredPlan) {
      const current = data?.currentPlan || "free";
      return `This feature requires ${data.requiredPlan}. Your current plan is ${current}.`;
    }
    return "Your current plan does not include this feature. Please upgrade your plan.";
  }

  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message;
  }

  return code || fallback;
}
